// TODO: Optimise URLSearchParams
import React, { useEffect, useMemo, useState } from 'react';
import Grid from '@components/core/layout/Grid';
import Card from '@components/Card';
import Pagination from '@components/core/Pagination';
import Filters from '@components/Filters';
import Link from 'next/link';
import { useRouter } from 'next/router';

// Part before LookUp are keys and most be one of the domains
const lookUps = {
  kinderopvang: {
    BSO: 'Buitenschoolse opvang',
    KDV: 'Kinderdagverblijf',
    POV: 'Peuteropvang',
  },
};

// Type definitions
interface ProductTypeOption {
  label: string;
  value: string;
}

interface LocationState {
  value: string;
  isValid: boolean;
  validState: Record<string, boolean>;
  showErrorMessage: boolean;
}

const getDistance = (lat1, lon1, lat2, lon2, unit) => {
  if ((lat1 == lat2) && (lon1 == lon2)) {
    return 0;
  }
  else {
    const radlat1 = Math.PI * lat1/180;
    const radlat2 = Math.PI * lat2/180;
    const theta = lon1-lon2;
    const radtheta = Math.PI * theta/180;
    let dist = Math.sin(radlat1) * Math.sin(radlat2) + Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta);
    if (dist > 1) {
      dist = 1;
    }
    dist = Math.acos(dist);
    dist = dist * 180/Math.PI;
    dist = dist * 60 * 1.1515;
    if (unit=='K') { dist = dist * 1.609344; }
    if (unit=='N') { dist = dist * 0.8684; }
    return dist;
  }
};

export default function Overview(props) {
  const domain = props.domain;
  const itemsPerPage = 12;
  const router = useRouter();
  const [ forceLoad, setForceLoad ] = useState(false);
  // Initialize page from router.query to ensure consistency between server and client
  const [ selectedPage, setPage ] = useState(() => {
    const pageParam = router.query?.page;
    return pageParam ? parseInt(Array.isArray(pageParam) ? pageParam[0] : pageParam) : 1;
  });
  const [ selectedProductType, setProductType ] = useState<ProductTypeOption[] | null>(null);
  const [ selectedLocation, setLocation ] = useState<LocationState>({
    value: props.city || '',
    isValid: true,
    validState: { }, // key: type of validation, value: boolean
    showErrorMessage: false,
  });

  const [ buttonIsDisabled, setButtonIsDisabled ] = useState(true);
  const [ activateFilter, toggleActiveFilter ] = useState(false);

  useEffect(() => {
    setForceLoad(f => !f);
  }, [ router ]);

  const handleEmitLocation = (e) => {
    setLocation({
      value: e.target.value,
      isValid: true,
      validState: { }, // key: type of validation, value: boolean
      showErrorMessage: false,
    });
    setButtonIsDisabled(false);
  };

  const getCurrentGeolocation = async(zipcodeValue: string) => {
    try {
      const response = await fetch('/api/geocode', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ zipcode: zipcodeValue }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Geocoding API error:', errorData);
        return null;
      }

      const data = await response.json();
      return {
        lat: data.lat,
        lng: data.lng,
      };
    } catch (error) {
      console.error('Error fetching geolocation:', error);
      return null;
    }
  };

  /* Not for onload */
  const applyFilter = async() => {
    const query = { ...router.query };

    // Set distance
    if (selectedLocation.value && selectedLocation.value.length > 0 && selectedLocation.isValid && !props.city) {
      const currentGeolocation = await getCurrentGeolocation(selectedLocation.value);
      if (currentGeolocation) {
        setLocationsWithZipcode(prevLocations => {
          return prevLocations.map((location) => {
            if (location.zipcode !== null) {
              const distance = getDistance(
                location.geoLocation?.lat,
                location.geoLocation?.lng,
                currentGeolocation.lat,
                currentGeolocation.lng,
                'K'
              );
              return {
                ...location,
                zipcode: {
                  ...location.zipcode,
                  distance,
                },
              };
            }
            return location;
          });
        });
        query.location = selectedLocation.value;
      } else {
        setLocation((prevState) => ({
          ...prevState,
          ...{ isValid: false },
          ...{ validState: {
            pattern: false,
          } },
          ...{ showErrorMessage: true },
        }));
      }
    } else {
      setLocationsWithZipcode(prevLocations => {
        return prevLocations.map((location) => {
          if (location.zipcode) {
            return {
              ...location,
              zipcode: {
                ...location.zipcode,
                distance: '',
              },
            };
          }
          return location;
        });
      });
      setLocation({
        value: props.city || '',
        isValid: true,
        validState: { }, // key: type of validation, value: boolean
        showErrorMessage: false,
      });
      delete query.location;
    }

    // Set URL params for product type
    if ( selectedProductType && Array.isArray(selectedProductType) && selectedProductType.length !== Object.keys(lookUps[domain]).length) {
      query.productType = selectedProductType.map((productTypeOption) => productTypeOption.value).join();
    } else {
      delete query.productType;
    }

    if (selectedPage !== 1) {
      setPage(1);
    }

    query.page = '1';

    router.push({
      pathname: router.pathname,
      query,
    }, undefined, { shallow: true });

    toggleActiveFilter(a => !a);
    setButtonIsDisabled(true);
  };

  // Create locations with zipcode - use state so mutations trigger re-renders
  const [ locationsWithZipcode, setLocationsWithZipcode ] = useState(() => {
    return props.locations.map((location) => {
      const locationCopy = { ...location };
      locationCopy.zipcode = null;
      if (locationCopy.sectionBox ) {
        const sectionIndex = locationCopy.sectionBox.findIndex((section) => section._type === 'multiContactSection');
        // Has no section that holds product data
        if ( sectionIndex !== -1 && locationCopy.sectionBox[sectionIndex].products?.[0]?.contactDetails ) {
          locationCopy.zipcode = {
            code: locationCopy.sectionBox[sectionIndex].products?.[0]?.contactDetails?.zipCode,
            distance: null, // Already set prop for google distance service relative to user zipcode
          };
        }
      }
      return locationCopy;
    });
  });

  // Update locations when props change
  useEffect(() => {
    setLocationsWithZipcode(props.locations.map((location) => {
      const locationCopy = { ...location };
      locationCopy.zipcode = null;
      if (locationCopy.sectionBox ) {
        const sectionIndex = locationCopy.sectionBox.findIndex((section) => section._type === 'multiContactSection');
        // Has no section that holds product data
        if ( sectionIndex !== -1 && locationCopy.sectionBox[sectionIndex].products?.[0]?.contactDetails ) {
          locationCopy.zipcode = {
            code: locationCopy.sectionBox[sectionIndex].products?.[0]?.contactDetails?.zipCode,
            distance: null, // Already set prop for google distance service relative to user zipcode
          };
        }
      }
      return locationCopy;
    }));
  }, [ props.locations ]);

  /**
   * On load:
   * Set states based on searchParams
   * Load Google service
   */
  useEffect(() => {
    // Set states based on router.query (consistent between server and client)
    const pageParam = router.query?.page;
    if (pageParam && pageParam !== '1') {
      setPage(parseInt(Array.isArray(pageParam) ? pageParam[0] : pageParam));
    }

    // Set product type based on param
    const productTypeParams = router.query?.productType;
    if (productTypeParams && typeof productTypeParams === 'string' && productTypeParams.length > 0) {
      const productTypeOptions = decodeURIComponent(productTypeParams).split(',')
        .map( (productTypeParam) => {
          return {
            label: lookUps[domain][productTypeParam],
            value: productTypeParam,
          };
        });
      setProductType(productTypeOptions);
    }

    // Set zipcode based on param
    const locationParams = router.query?.location;
    if (locationParams && typeof locationParams === 'string' && locationParams.length > 0) {
      setLocation({
        value: locationParams,
        isValid: true,
        validState: { pattern: true }, // key: type of validation, value: boolean
        showErrorMessage: false,
      });
      const asyncSetDistance = async() => {
        const currentGeolocation = await getCurrentGeolocation(locationParams);
        if (currentGeolocation) {
          setLocationsWithZipcode(prevLocations => {
            return prevLocations.map((location) => {
              if (location.zipcode) {
                const distance = getDistance(
                  location.geoLocation?.lat,
                  location.geoLocation?.lng,
                  currentGeolocation.lat,
                  currentGeolocation.lng,
                  'K'
                );
                return {
                  ...location,
                  zipcode: {
                    ...location.zipcode,
                    distance,
                  },
                };
              }
              return location;
            });
          });
        } else {
          setLocation((prevState) => ({
            ...prevState,
            ...{ isValid: false },
            ...{ validState: {
              pattern: false,
            } },
            ...{ showErrorMessage: true },
          }));
        }
        toggleActiveFilter(a => !a);
        setButtonIsDisabled(true);
      };

      asyncSetDistance();
    } else {
      setLocation({
        value: props.city || '',
        isValid: false,
        validState: {
          pattern: false,
        }, // key: type of validation, value: boolean
        showErrorMessage: false,
      });
      toggleActiveFilter(a => !a);
      setButtonIsDisabled(true);
    }

    return;
  }, [ forceLoad, router.query, domain, props.city ]);

  const filteredLocations = useMemo(() => {
    const montrisLocations = locationsWithZipcode.filter((location) => location.pageTitle.startsWith('Montris'));
    return locationsWithZipcode
      .filter((location) => !location.pageTitle.startsWith('Montris'))
      .concat(montrisLocations)
      .filter((location) => { // Filter on selected product type
        const noProductTypeFilter =
        !selectedProductType || !Array.isArray(selectedProductType) ||
        selectedProductType.length === 0 || selectedProductType.length === Object.keys(lookUps[domain]).length;
        if (noProductTypeFilter) {
          return true;
        }

        // Has any section
        if (location.sectionBox ) {
          const sectionIndex = location.sectionBox.findIndex((section) => section._type === 'multiContactSection');
          // Has no section that holds product data
          if ( sectionIndex === -1) {
            return false;
          }
          // Has product data
          let hasSelectedProduct = false;
          if (location.sectionBox[sectionIndex].products) {
            for (const product of location.sectionBox[sectionIndex].products) {
              if (selectedProductType) {
                for (const productType of selectedProductType) {
                  if (product.productName === productType.value) {
                    hasSelectedProduct = true;
                  }
                }
              }
            }
          }
          return hasSelectedProduct;
        } else {
          return false;
        }
      })
      .sort((a, b) => {
        if (a.zipcode && a.zipcode.distance ) {
          if (b.zipcode && b.zipcode.distance) {
            if (a.zipcode.distance < b.zipcode.distance) {
              return  -1;
            } else if (a.zipcode.distance > b.zipcode.distance) {
              return 1;
            } else {
              return 0;
            }
          } else {
            return -1;
          }
        } else {
          return 0;
        }
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ locationsWithZipcode, activateFilter, domain ]);

  /* Update page query without re-render */
  function selectPage(page: number) {
    const query = { ...router.query, page: page.toString() };
    router.push({
      pathname: router.pathname,
      query,
    }, undefined, { shallow: true });
    setPage(page);
  }

  return (
    <section className='pt-8 md:pt-16 xl:pt-24 pb-0'>
      {
        domain === 'kinderopvang' &&
        <>
          <Grid>
            <div className='col-span-12 text-heading-xs mb-4'>Filter op uw voorkeuren
              <span className='text-shade'> ({ filteredLocations.length } resulta{ filteredLocations.length > 1 ? 'ten' : 'at'})</span>
            </div>
          </Grid>
          <div className='mb-6 md:mb-8 xl:mb-12'>
            <Grid>
              <div className='col-span-12 flex flex-col lg:flex-row'>
                <Filters
                  position='overview'
                  options={ {
                    productTypes: lookUps[domain],
                  } }
                  selectedProductType={ selectedProductType }
                  selectedLocation={ selectedLocation }
                  buttonIsDisabled={ buttonIsDisabled }
                  emit={ {
                    productType: setProductType,
                    location: handleEmitLocation,
                    applyFilter,
                    disableApplyButton: setButtonIsDisabled,
                  } }
                  disableLocation={ !!props.city }
                  buttonLabel='Pas filters toe'
                />
              </div>
            </Grid>
          </div>
        </>
      }
      <Grid>
        { filteredLocations
          .map((location) => {
            return (
              <div key={ location._id } className='col-span-12 md:col-span-6 xl:col-span-4 mb-6 md:mb-8 xl:mb-12'>
                <Link href={ `/kinderopvang-locaties/${ location.parentSlug }/${ location.slug.current }` } key={ location._id }>
                  <Card  location={ location } productLookUp={ lookUps[domain] } domain={ props.domain } />
                </Link>
              </div>
            );
          }).slice(Math.max(0, (selectedPage * itemsPerPage) -  itemsPerPage), selectedPage * itemsPerPage)}
        <Pagination
          dataLength={ filteredLocations.length }
          itemsPerPage={ itemsPerPage }
          currentPage={ selectedPage }
          setCurrentPage={ selectPage }
          className='col-span-6 col-start-4 mb-6 md:mb-8 xl:mb-12'
        />
      </Grid>
    </section>
  );
}
